import { trackSwapRandomTestBuyArticle } from "@coworker/app/src/helpers/tracker";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { Trans } from "@coworker/locales";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import profilePreferences from "@coworker/enums/profilePreferences";
import { replaceTestbuyArticle } from "../../services/testbuy.service";
import { Testbuy } from "@coworker/apprestructured/src/testbuy/services/testbuy.service";
import { replaceTestbuyArticleAndRefetch } from "../../services/queryKeyHelper";
import { TestBuyProductWithCriteria } from "@coworker/types/lib/testbuy";
import { HttpError } from "@coworker/http-client";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { useTranslation } from "react-i18next";

interface GetNewButtonProps {
  testbuy: Testbuy;
  article: TestBuyProductWithCriteria;
  area: string;
  onLoadingChange?: (loading: boolean) => void;
  onClose?: () => void;
}

export const GetNewButton = ({
  testbuy,
  article,
  area,
  onLoadingChange,
  onClose,
}: GetNewButtonProps) => {
  // Hooks
  const { showToast } = useToastNotification();
  const store = useMyStore();
  const [userLanguage] = useUserPreference(
    profilePreferences.LANGUAGE,
    store?.primaryLocale
  );
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const replaceArticleMutation = useMutation({
    mutationFn: async () => {
      if (!article.id) return null;
      return replaceTestbuyArticle(testbuy.id, article.id, {
        locale: userLanguage,
        countryId: store?.countryId ?? "",
        area,
        excludedArticleShortId: testbuy.articles.map((a) => a.shortId),
      });
    },
    onSuccess: async (newArticle) => {
      if (newArticle) {
        await replaceTestbuyArticleAndRefetch(
          queryClient,
          testbuy.id,
          article.shortId,
          newArticle.shortId
        );
      }
    },
  });

  // Event handlers
  const handleGetNewArticle = async () => {
    trackSwapRandomTestBuyArticle(testbuy.strategy, article.shortId, area);
    onLoadingChange?.(true);

    try {
      await replaceArticleMutation.mutateAsync();
      onClose?.();
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        showToast(
          <Trans>noArticlesAvailableToReplaceString</Trans>,
          undefined,
          undefined,
          5
        );
      } else {
        showToast(<Trans>taskErrorMessage</Trans>, undefined, undefined, 5);
      }
    } finally {
      onLoadingChange?.(false);
    }
  };

  if (!article.id) return null;
  return (
    <FixaListViewItem
      title={t("randomizeString")}
      control="navigational"
      controlIcon={null}
      onClick={handleGetNewArticle}
      disabled={replaceArticleMutation.isPending}
      emphasised
    />
  );
};
